import { useApolloClient } from '@apollo/client';
import { Box, Button, Flex, Progress, Select, Stack } from '@eleveo/components-react';
import { BellIcon, PaperPenIcon, RefreshIcon, WarningIcon } from '@eleveo/icons-react';
// eslint-disable-next-line
import { getLocalTimeZone, now, parseAbsoluteToLocal } from '@internationalized/date';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { useAuth, Permissions } from '../../app/AuthorizationProvider';
import { useConfig } from '../../app/ConfigurationProvider';
import { DateFormatter } from '../../components/DateFormatter';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  GetIntradayPushNotificationOnOffSettingDocument,
  useGetIntradayPerQueueDataQuery,
  useGetIntradayQueuesListQuery,
  useGetProductionSchedulesPerQueuesLazyQuery,
  useIntradayMeasurementsChangedSubscription,
} from '../../graphql/components';
import { PATH_INTRADAY_MANAGEMENT, PATH_SCHEDULING_STUDIO, PATH_SCHEDULING_STUDIO_EDIT } from '../../router/routes';
import { usePrevious } from '../../utils/usePrevious';
import { ONE_HOUR } from '../Scheduling/config';
import { IntradayChartTile } from './IntradayChartTile';
import { IntradayNotificationsSidebar } from './IntradayNotificationsSidebar';
import { intradayQueuesToOptions, intradayQueuesValueFormatter } from './NotificationForm';
import { intradayChartsTypesOrdered } from './config';
import {
  CONTAINER_TAG,
  DATA_AS_OF_TAG,
  GO_TO_SCHEDULE_BUTTON_TAG,
  INTRADAY_QUEUE_SELECT_TAG,
  NOTIFICATIONS_TOGGLE_BUTTON_TAG,
  REFRESH_DATA_BUTTON_TAG,
  TITLE_TAG,
} from './constants';
import { generateChartMetadata } from './utils';

export const IntradayManagementScene: React.FC = () => {
  const { t } = useTranslation();
  const { queueId: queueIdToView } = useParams<{ queueId?: string }>();
  const routerHistory = useHistory();
  const { appUrl } = useConfig();
  const { userHasPermissions } = useAuth();

  const previousQueueId = usePrevious(queueIdToView);

  React.useEffect(() => {
    // Redirect back to the same queue when navigating to the root URL via Navigation item (no queueId param in the URL).
    if (queueIdToView === undefined && previousQueueId !== undefined) {
      routerHistory.replace(`${PATH_INTRADAY_MANAGEMENT}/${previousQueueId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousQueueId, queueIdToView]);

  const hasEditPermission = userHasPermissions([Permissions.EditIntradayNotification]);
  const hasViewPermission = userHasPermissions([Permissions.ViewIntradayNotification]);
  const hasViewSchedulePermission = userHasPermissions([Permissions.ViewSchedule]);

  const { toggleSidebar, closeSidebar, isSidebarOpen } = useSidebar();

  const {
    data: queuesListData,
    loading: queuesListLoading,
    error: queuesListError,
    refetch: refetchQueuesListData,
  } = useGetIntradayQueuesListQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: ({ intradayQueuesList }) => {
      // Initial Queue Select value setup on first render pre-sorted on BE side thus first[0] selected
      if (!queueIdToView && intradayQueuesList?.length) {
        routerHistory.replace(`${PATH_INTRADAY_MANAGEMENT}/${intradayQueuesList[0].id}`);
      }
    },
  });

  const {
    data: intradayPerQueueData,
    loading: intradayPerQueueLoading,
    error: intradayPerQueueError,
    refetch: refetchIntradayPerQueueData,
  } = useGetIntradayPerQueueDataQuery({
    skip: !queueIdToView,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    variables: {
      // @ts-expect-error request should be skipped if queueId is not defined
      queueId: queueIdToView,
    },
  });

  const [
    loadProductionSchedulesPerQueueData,
    { loading: productionSchedulesPerQueueLoading, error: productionSchedulesPerQueueError },
  ] = useGetProductionSchedulesPerQueuesLazyQuery({ fetchPolicy: 'network-only' });

  const client = useApolloClient();

  const chartsMetadata = useMemo(
    () => generateChartMetadata(intradayPerQueueData?.intradayPerQueueData),
    [intradayPerQueueData]
  );

  const loading = queuesListLoading || intradayPerQueueLoading || productionSchedulesPerQueueLoading;
  const error = queuesListError || intradayPerQueueError || productionSchedulesPerQueueError;

  const queuesList = queuesListData?.intradayQueuesList ?? [];
  const { dataAsOf, actualInterval, timezone } = intradayPerQueueData?.intradayPerQueueData?.info ?? {};

  const dataAsOfZonedDate = dataAsOf ? parseAbsoluteToLocal(dataAsOf) : undefined;
  const actualIntervalZonedDate = actualInterval ? parseAbsoluteToLocal(actualInterval) : undefined;

  const localTimezone = getLocalTimeZone();
  const dataAsOfDate = dataAsOfZonedDate ? dataAsOfZonedDate.toDate() : undefined;
  const actualIntervalDate = actualIntervalZonedDate ? actualIntervalZonedDate.toDate() : undefined;

  const dataIntegrityError =
    actualIntervalDate && dataAsOfDate
      ? Math.abs(actualIntervalDate.getTime() - dataAsOfDate.getTime()) >= ONE_HOUR
      : false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedOptions = useMemo(() => intradayQueuesToOptions(queuesList, t), [queuesList]);
  const memoizedValueFormatter = useMemo(() => intradayQueuesValueFormatter(t), [t]);

  const handleGoToSchedule = async () => {
    if (!queueIdToView) {
      return;
    }

    const start = now(localTimezone).set({ second: 0, millisecond: 0 });
    const end = start.add({ minutes: 1 });

    const { data: { productionSchedulesPerQueues } = {} } = await loadProductionSchedulesPerQueueData({
      variables: {
        queueIds: queueIdToView,
        endDateTime: end.toAbsoluteString(),
        startDateTime: start.toAbsoluteString(),
      },
    });

    if (productionSchedulesPerQueues?.length) {
      const { scheduleId } = productionSchedulesPerQueues[0];
      const scheduleLandingPath =
        productionSchedulesPerQueues.length === 1
          ? `${appUrl}${PATH_SCHEDULING_STUDIO_EDIT}/${scheduleId}`
          : `${appUrl}${PATH_SCHEDULING_STUDIO}/?queueId=${queueIdToView}&date=${start.toAbsoluteString()}`;

      window.open(scheduleLandingPath, '_blank');
    } else {
      toast.info(t('intraday.noProductionSchedule'));
    }
  };

  const handleRefetchIntradayPerQueueData = async () => {
    if (!queueIdToView) {
      return;
    }

    try {
      refetchIntradayPerQueueData({
        queueId: queueIdToView,
      });

      refetchQueuesListData();
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  useIntradayMeasurementsChangedSubscription({
    onData: () => {
      handleRefetchIntradayPerQueueData();
    },
  });

  const startZonedDateTime = actualIntervalZonedDate ? actualIntervalZonedDate.subtract({ hours: 13 }) : undefined;
  const endZonedDateTime = actualIntervalZonedDate ? actualIntervalZonedDate.add({ hours: 13 }) : undefined;

  // TODO: Remove when used in code
  console.log(error);

  return (
    <>
      <Header title={t('intraday.intradayManagement')} testId={TITLE_TAG} />
      <Panel>
        <Flex alignItems="center" gap={15} wrap>
          <Stack spacing={15} className="flex-1">
            <Select
              label={t('queue.queue')}
              options={memoizedOptions}
              onSelectionChange={(values) => {
                routerHistory.push(`${PATH_INTRADAY_MANAGEMENT}/${values.values().next().value}`);
              }}
              selectedKeys={
                queueIdToView &&
                // Avoid Select warning when options are not present yet during back/forward browser navigation
                memoizedOptions.length > 0
                  ? [queueIdToView]
                  : []
              }
              selectionMode="single"
              testId={INTRADAY_QUEUE_SELECT_TAG}
              valueFormatter={memoizedValueFormatter}
            />
            {hasViewSchedulePermission && (
              <Button
                isDisabled={!queueIdToView}
                onClick={handleGoToSchedule}
                testId={GO_TO_SCHEDULE_BUTTON_TAG}
                variant="outline"
              >
                <PaperPenIcon />
                {t('intraday.goToSchedule')}
              </Button>
            )}
          </Stack>

          <Box
            background={{
              light: 'blue-400',
            }}
            className="intraday__data-as-of flex items-center"
            padding={10}
            rounded
            testId={DATA_AS_OF_TAG}
          >
            {t('intraday.chartsRefresh')}
            <strong> {t('intraday.every15Minutes')}.</strong>
            {dataAsOfDate && (
              <>
                {' '}
                {t('intraday.dataAsOf')}{' '}
                <DateFormatter
                  className={clsx('font-bold', {
                    'text-red-900': dataIntegrityError,
                  })}
                  options={{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: timezone,
                  }}
                >
                  {dataAsOfDate}
                </DateFormatter>
                {dataIntegrityError && <WarningIcon className="text-red-900" />}
              </>
            )}
          </Box>
          <Button
            aria-label={t('filter.refresh')}
            onClick={handleRefetchIntradayPerQueueData}
            testId={REFRESH_DATA_BUTTON_TAG}
          >
            <RefreshIcon />
          </Button>

          <div className="flex flex-1 justify-end">
            {(hasViewPermission || hasEditPermission) && (
              <Button
                isSelected={isSidebarOpen}
                onClick={() => {
                  toggleSidebar();
                  // Refetch data in PushNotificationProvider to get the fresh state
                  client.refetchQueries({
                    include: [GetIntradayPushNotificationOnOffSettingDocument],
                  });
                }}
                testId={NOTIFICATIONS_TOGGLE_BUTTON_TAG}
                variant="outline"
              >
                <BellIcon />
                {t('intraday.notifications')}
              </Button>
            )}
          </div>
        </Flex>
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {loading && <Progress withOverlay />}
        <div className="intraday__charts-container" data-test-id="intraday:charts-container">
          {intradayChartsTypesOrdered.map((chartType) => (
            <IntradayChartTile
              data={intradayPerQueueData?.intradayPerQueueData?.charts[chartType]?.series}
              dataAsOf={actualIntervalZonedDate}
              endDateTime={endZonedDateTime}
              chartType={chartType}
              isLoading={queuesListLoading || intradayPerQueueLoading}
              key={chartType}
              metadata={chartsMetadata[chartType]}
              startDateTime={startZonedDateTime}
              timezone={timezone}
            />
          ))}
        </div>
      </Content>
      <SidebarWrapper>
        {isSidebarOpen && (
          <IntradayNotificationsSidebar
            handleCancel={closeSidebar}
            hasEditPermission={hasEditPermission}
            queuesList={memoizedOptions}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
